import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { AlertCircle } from 'lucide-react';
import AppSpinner from "../Spinner/AppSpinner";
import DataLoader from "../Spinner/DataLoader";
import GroupFilter from '../GroupFilter/GroupFilter';
import GroupsList from '../GroupList/GroupList';
import headerStyles from './HeaderForTeacherGroupsPage.module.css';
import axiosInstance from '../../utils/axiosInstance';
import { BASE_URL } from '../../constants';
import { apiFetch } from '../../utils/apiFetch';
import PageInfoModal from '../PageInfoModal/PageInfoModal';

const GROUPS_INFO_ITEMS = [
  { icon: 'collection', text: 'Aktiv və deaktiv qruplarınızı "Aktiv / Deaktiv" filtiri ilə görə bilərsiniz.' },
  { icon: 'arrow-right-circle', text: 'Hər qrupun üzərinə klik edərək tələbə siyahısını, davamiyyət və qiymət məlumatlarını aça bilərsiniz.' },
  { icon: 'arrow-repeat', text: 'Yenilə düyməsi ilə qrup siyahısını manual olaraq yeniləyə bilərsiniz.' },
  { icon: 'lightbulb', text: '"Məlumat" (sarı) düyməsinə klik edərək dərs axını və quiz haqqında məlumat ala bilərsiniz.' },
  { icon: 'bell-fill', text: 'Zəng (🔔) düyməsi ilə sizə təyin edilmiş bildirişlərə baxa və onları oxudu olaraq işarələyə bilərsiniz.' },
]

const cleanHelpHtml = (rawHtml = "") => {
  if (typeof window === "undefined" || !window.DOMParser) return rawHtml;
  try {
    const parser = new window.DOMParser();
    const doc = parser.parseFromString(String(rawHtml), "text/html");
    doc.querySelectorAll("script, style, meta, link, title, head").forEach((node) => node.remove());
    return doc.body?.innerHTML?.trim() || "";
  } catch {
    return String(rawHtml);
  }
};

const HelpModal = ({ show, onClose, isLoading, items }) => {
  if (!show) return null;
  if (typeof document === "undefined") return null;

  return createPortal(
    <div className={headerStyles.helpOverlay} onClick={onClose} role="presentation">
      <div
        className={headerStyles.helpDialog}
        role="dialog"
        aria-modal="true"
        aria-label="Məlumat"
        onClick={(event) => event.stopPropagation()}
      >
        <div className={headerStyles.helpModalHeader}>
          <div className={headerStyles.helpModalHeaderLeft}>
            <h6 className={headerStyles.helpModalTitle}>
              <span className={headerStyles.helpModalIcon}>📋</span>
              Məlumat
            </h6>
            <span className={headerStyles.helpModalSubtitle}>Qısa və vacib qeydlər</span>
          </div>
          <button
            type="button"
            className={headerStyles.helpCloseX}
            onClick={onClose}
            aria-label="Bağla"
          >
            ×
          </button>
        </div>
        <div className={headerStyles.helpModalBody}>
          {isLoading ? (
            <div className="text-center py-4">
              <AppSpinner inline />
            </div>
          ) : items.length > 0 ? (
            <ol className={headerStyles.helpList}>
              {items.map((item, index) => (
                <li key={item.id} className={headerStyles.helpListItem}>
                  <span className={headerStyles.helpItemNumber}>{index + 1}</span>
                  <div className={headerStyles.helpItemText} dangerouslySetInnerHTML={{ __html: cleanHelpHtml(item.description) }} />
                </li>
              ))}
            </ol>
          ) : (
            <p className="text-muted mb-0 text-center py-3">Məlumat tapılmadı.</p>
          )}
        </div>
        <div className={headerStyles.helpModalFooter}>
          <button type="button" className={headerStyles.helpModalCloseBtn} onClick={onClose}>
            Anladım
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const InfosModal = ({ show, onClose, items, onConfirm, confirmingId }) => {
  if (!show) return null;
  return createPortal(
    <div className={headerStyles.helpOverlay} onClick={onClose} role="presentation">
      <div className={headerStyles.helpDialog} role="dialog" aria-modal="true" onClick={e => e.stopPropagation()}>
        <div className={headerStyles.helpModalHeader}>
          <div className={headerStyles.helpModalHeaderLeft}>
            <h6 className={headerStyles.helpModalTitle}>
              <span className={headerStyles.helpModalIcon}>🔔</span>
              Bildirişlər
            </h6>
            <span className={headerStyles.helpModalSubtitle}>Son yenilənmələr və qeydlər</span>
          </div>
          <button type="button" className={headerStyles.helpCloseX} onClick={onClose}>×</button>
        </div>
        <div className={headerStyles.helpModalBody}>
          {items.length === 0 ? (
            <p className="text-muted mb-0 text-center py-3">Bildiriş tapılmadı.</p>
          ) : (
            <div className={headerStyles.infoList}>
              {items.map(item => (
                <div key={item.id} className={`${headerStyles.infoItem} ${item.confirmed ? headerStyles.infoItemRead : ''}`}>
                  <div className={headerStyles.infoItemHeader}>
                    <span className={headerStyles.infoItemName}>{item.name}</span>
                    {item.confirmed ? (
                      <span className={headerStyles.infoBadgeRead}>
                        <i className="bi bi-check-circle-fill me-1"></i>Oxunub
                      </span>
                    ) : (
                      <button
                        className={headerStyles.infoBadgeUnread}
                        onClick={() => onConfirm(item)}
                        disabled={confirmingId === item.id}
                      >
                        {confirmingId === item.id
                          ? <><AppSpinner inline /> Gözlə...</>
                          : <><i className="bi bi-check2 me-1"></i>Oxudum</>
                        }
                      </button>
                    )}
                  </div>
                  <div className={headerStyles.infoItemDesc} dangerouslySetInnerHTML={{ __html: cleanHelpHtml(item.description) }} />
                </div>
              ))}
            </div>
          )}
        </div>
        <div className={headerStyles.helpModalFooter}>
          <button type="button" className={headerStyles.helpModalCloseBtn} onClick={onClose}>Bağla</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const ConfirmReadModal = ({ show, onClose, onConfirm }) => {
  if (!show) return null;
  return createPortal(
    <div className={headerStyles.helpOverlay} onClick={onClose} role="presentation" style={{ zIndex: 100000 }}>
      <div className={headerStyles.confirmReadDialog} onClick={e => e.stopPropagation()}>
        <div className={headerStyles.confirmReadIconWrap}>
          <i className="bi bi-check-circle" style={{ fontSize: 26, color: '#059669' }}></i>
        </div>
        <h6 className={headerStyles.confirmReadTitle}>Təsdiqləmək istəyirsiniz?</h6>
        <p className={headerStyles.confirmReadText}>Bu bildirişi oxuduğunuzu təsdiqləyirsiniz?</p>
        <div className={headerStyles.confirmReadActions}>
          <button onClick={onClose} className={headerStyles.confirmReadBtnCancel}>Ləğv et</button>
          <button onClick={onConfirm} className={headerStyles.confirmReadBtnOk}>Əminəm</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const HeaderForTeacherGroupsPage = ({
  filterTab = 'aktiv',
  setFilterTab = () => {},
  onRefresh = () => {},
  groupsData = [],
  isDataLoading = false
}) => {
  const [activeHeaderTab, setActiveHeaderTab] = useState('groups');
  const [isSwitchingTab, setIsSwitchingTab] = useState(false);
  const [showQuizInfoModal, setShowQuizInfoModal] = useState(false);
  const [helpItems, setHelpItems] = useState([]);
  const [isHelpLoading, setIsHelpLoading] = useState(false);

  const [showInfosModal, setShowInfosModal] = useState(false);
  const [infos, setInfos] = useState([]);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [confirmingId, setConfirmingId] = useState(null);

  const fetchInfos = useCallback(async () => {
    try {
      const res = await apiFetch(`${BASE_URL}/teacher-helps/my-assigned-infos`);
      const data = await res.json();
      setInfos((data.infos || []).sort((a, b) => b.id - a.id));
    } catch {
      setInfos([]);
    }
  }, []);

  useEffect(() => { fetchInfos(); }, [fetchInfos]);

  const unreadCount = infos.filter(i => !i.confirmed).length;

  const handleConfirmClick = (item) => setConfirmTarget(item);

  const handleConfirmSubmit = async () => {
    if (!confirmTarget) return;
    setConfirmingId(confirmTarget.id);
    setConfirmTarget(null);
    try {
      await apiFetch(`${BASE_URL}/teacher-helps/confirm-read`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ infoId: confirmTarget.id }),
      });
      setInfos(prev => prev.map(i => i.id === confirmTarget.id ? { ...i, confirmed: true } : i));
    } catch {
    } finally {
      setConfirmingId(null);
    }
  };

  const openQuizInfoModal = async () => {
    setShowQuizInfoModal(true);
    setIsHelpLoading(true);
    try {
      const res = await axiosInstance.get(`${BASE_URL}/helps/basic-information-for-teachers`);
      const data = res.data;
      const list = Array.isArray(data)
        ? data
        : Object.values(data ?? {}).find(Array.isArray) ?? [];
      setHelpItems(list);
    } catch {
      setHelpItems([]);
    } finally {
      setIsHelpLoading(false);
    }
  };

  const handleTabChange = (tabName) => {
    if (tabName === activeHeaderTab) return;

    setIsSwitchingTab(true);
    setTimeout(() => {
      setActiveHeaderTab(tabName);
      setIsSwitchingTab(false);
    }, 400);
  };

  return (
    <div className="teacher-groups-wrapper" style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <header className="d-flex align-items-center gap-2 mb-3 flex-wrap" style={{ flexShrink: 0 }}>
        <div className="d-flex align-items-center gap-2">
          <PageInfoModal title="Qruplar" items={GROUPS_INFO_ITEMS} />
          <button
            type="button"
            className={headerStyles.quizInfoBtn}
            onClick={() => handleTabChange('groups')}
            disabled={isSwitchingTab}
          >
            Qruplarım
          </button>
          <span className={headerStyles.quizInfoBtnWrap}>
            <button
              type="button"
              className={headerStyles.quizInfoBtn}
              onClick={openQuizInfoModal}
              title="Bu düyməyə click etməklə, əsas lazım olan son məlumatları ala bilərsiniz."
              aria-label="Quizlər və dərs axını haqqında məlumat"
            >
              <AlertCircle size={15} strokeWidth={2.4} />
              <span>Məlumat</span>
            </button>
          </span>
          <span className={headerStyles.notifBtnWrap}>
            <button
              type="button"
              className={`${headerStyles.notifBtn} ${unreadCount > 0 ? headerStyles.notifBtnActive : ''}`}
              onClick={() => setShowInfosModal(true)}
              title="Bildirişlər"
            >
              <i className={`bi bi-bell-fill ${unreadCount > 0 ? headerStyles.bellShake : ''}`}></i>
              {unreadCount > 0 && (
                <span className={headerStyles.notifBadge}>{unreadCount}</span>
              )}
            </button>
          </span>
        </div>
      </header>
      <HelpModal
        show={showQuizInfoModal}
        onClose={() => setShowQuizInfoModal(false)}
        isLoading={isHelpLoading}
        items={helpItems}
      />
      <InfosModal
        show={showInfosModal}
        onClose={() => setShowInfosModal(false)}
        items={infos}
        onConfirm={handleConfirmClick}
        confirmingId={confirmingId}
      />
      <ConfirmReadModal
        show={!!confirmTarget}
        onClose={() => setConfirmTarget(null)}
        onConfirm={handleConfirmSubmit}
      />

      <div className="component-chg border-top rounded p-3"
           style={{ flex: 1, overflowY: 'auto', minHeight: 0, width: "100%", position: "relative" }}>

        <DataLoader show={isSwitchingTab || isDataLoading} />

        {!isSwitchingTab && (
          <div className="w-100">
            {activeHeaderTab === 'groups' && (
              <div>
                <GroupFilter
                  activeTab={filterTab}
                  setActiveTab={setFilterTab}
                  onRefresh={onRefresh}
                  isLoading={isDataLoading}
                />

                {!isDataLoading && (
                  <div className="mt-3">
                    <p className="text-muted small mb-3">
                      Cəmi: <strong>{groupsData?.length || 0}</strong> qrup tapıldı.
                    </p>
                    <GroupsList
                      data={groupsData}
                      onRefresh={onRefresh}
                      isLoading={isDataLoading}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default HeaderForTeacherGroupsPage;
